import { useState, useEffect } from 'react';

/**
 * Returns up to two uppercase initials from a full name.
 * "Sarah Johnson" -> "SJ"
 * "Sarah"         -> "S"
 * ""              -> fallback (default "U")
 */
function getInitials(name, fallback = 'U') {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback.toUpperCase();
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * User avatar that gracefully falls back to initials if the image URL is
 * missing, empty, or fails to load (e.g., deleted file, 404, CORS).
 *
 * Intentionally renders a <div> wrapper so existing sizing/overflow classes
 * passed via `className` continue to work at every call site.
 */
export default function UserAvatar({
  src,
  name = '',
  fallback,
  className = '',
  imgClassName = 'w-full h-full object-cover block',
  alt = '',
  style,
  testId,
  imgTestId,
}) {
  const [errored, setErrored] = useState(false);

  // Reset the error state whenever the src changes so a new upload re-tries.
  useEffect(() => { setErrored(false); }, [src]);

  const showImage = src && !errored;
  const initial = getInitials(name, fallback);

  return (
    <div className={className} style={style} data-testid={testId}>
      {showImage ? (
        <img
          src={src}
          alt={alt || name}
          className={imgClassName}
          onError={() => setErrored(true)}
          data-testid={imgTestId}
        />
      ) : (
        <span data-testid="avatar-initials">{initial}</span>
      )}
    </div>
  );
}
